<script setup>
import { nextTick, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { PHOTO_CAPTURE_CONSTRAINTS } from './mediaCaptureHelper';

const props = defineProps({
  selectedVideoInputDeviceId: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['capture-photo', 'camera-error']);

const { t } = useI18n();

const dialogRef = ref(null);
const videoRef = ref(null);
const canvasRef = ref(null);
const streamRef = ref(null);
const isOpen = ref(false);
const isStartingCamera = ref(false);

const stopCamera = () => {
  streamRef.value?.getTracks().forEach(track => track.stop());
  streamRef.value = null;
  if (videoRef.value) {
    videoRef.value.srcObject = null;
  }
};

const getCameraErrorKey = error => {
  switch (error?.name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      return 'permission';
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'OverconstrainedError':
      return 'device_not_found';
    case 'NotReadableError':
    case 'TrackStartError':
      return 'device_unavailable';
    default:
      return 'generic';
  }
};

const startCamera = async () => {
  if (!navigator.mediaDevices?.getUserMedia) {
    emit('camera-error', { key: 'generic' });
    close();
    return;
  }

  isStartingCamera.value = true;
  stopCamera();

  try {
    const constraints = props.selectedVideoInputDeviceId
      ? {
          deviceId: {
            exact: props.selectedVideoInputDeviceId,
          },
          ...PHOTO_CAPTURE_CONSTRAINTS,
        }
      : {
          facingMode: 'environment',
          ...PHOTO_CAPTURE_CONSTRAINTS,
        };

    const stream = await navigator.mediaDevices.getUserMedia({
      video: constraints,
      audio: false,
    });

    streamRef.value = stream;
    if (videoRef.value) {
      videoRef.value.srcObject = stream;
      await videoRef.value.play();
    }
  } catch (error) {
    emit('camera-error', {
      key: getCameraErrorKey(error),
      errorName: error?.name || '',
    });
    close();
  } finally {
    isStartingCamera.value = false;
  }
};

const open = async () => {
  isOpen.value = true;
  dialogRef.value?.open();
  await nextTick();
  await startCamera();
};

const close = () => {
  dialogRef.value?.close();
};

const handleDialogClose = () => {
  stopCamera();
  isOpen.value = false;
};

const capturePhoto = () => {
  const video = videoRef.value;
  const canvas = canvasRef.value;
  const videoTrack = streamRef.value?.getVideoTracks?.()[0];

  if (videoTrack && 'ImageCapture' in window) {
    const imageCapture = new window.ImageCapture(videoTrack);
    imageCapture
      .takePhoto()
      .then(blob => {
        const extension = blob.type.includes('png') ? 'png' : 'jpg';
        const file = new File([blob], `photo-${Date.now()}.${extension}`, {
          type: blob.type || 'image/jpeg',
        });

        emit('capture-photo', {
          file,
          name: file.name,
          type: file.type,
          size: file.size,
        });
        close();
      })
      .catch(() => {
        emit('camera-error', { key: 'generic' });
      });
    return;
  }

  if (!video || !canvas || !video.videoWidth || !video.videoHeight) {
    emit('camera-error', { key: 'generic' });
    return;
  }

  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;

  const context = canvas.getContext('2d');
  context.drawImage(video, 0, 0, canvas.width, canvas.height);

  canvas.toBlob(
    blob => {
      if (!blob) {
        emit('camera-error', { key: 'generic' });
        return;
      }

      const file = new File([blob], `photo-${Date.now()}.jpg`, {
        type: 'image/jpeg',
      });

      emit('capture-photo', {
        file,
        name: file.name,
        type: file.type,
        size: file.size,
      });
      close();
    },
    'image/jpeg',
    0.98
  );
};

watch(
  () => props.selectedVideoInputDeviceId,
  async (nextDeviceId, previousDeviceId) => {
    if (!isOpen.value || nextDeviceId === previousDeviceId) {
      return;
    }

    await startCamera();
  }
);

onUnmounted(() => {
  stopCamera();
});

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="md"
    :title="t('CONVERSATION.REPLYBOX.CAMERA_CAPTURE_TITLE')"
    :description="t('CONVERSATION.REPLYBOX.CAMERA_CAPTURE_DESCRIPTION')"
    :show-confirm-button="false"
    :show-cancel-button="false"
    @close="handleDialogClose"
  >
    <div class="flex flex-col gap-4">
      <div
        class="camera-preview-wrap"
        :class="{ 'is-loading': isStartingCamera }"
      >
        <video ref="videoRef" autoplay playsinline muted class="camera-preview" />
        <div v-if="isStartingCamera" class="camera-preview__overlay">
          {{ t('CONVERSATION.REPLYBOX.CAMERA_CAPTURE_LOADING') }}
        </div>
      </div>
      <canvas ref="canvasRef" class="hidden" />
    </div>
    <template #footer>
      <div class="flex items-center justify-between gap-3">
        <Button
          type="button"
          variant="faded"
          color="slate"
          :label="t('DIALOG.BUTTONS.CANCEL')"
          class="w-full"
          @click="close"
        />
        <Button
          type="button"
          color="blue"
          :label="t('CONVERSATION.REPLYBOX.CAMERA_CAPTURE_BUTTON')"
          class="w-full"
          :disabled="isStartingCamera"
          @click="capturePhoto"
        />
      </div>
    </template>
  </Dialog>
</template>

<style scoped lang="scss">
.camera-preview-wrap {
  @apply relative overflow-hidden rounded-xl border border-n-container bg-n-alpha-2 min-h-72;
}

.camera-preview {
  @apply block w-full h-72 object-cover bg-n-slate-2;
}

.camera-preview__overlay {
  @apply absolute inset-0 flex items-center justify-center text-sm font-medium text-n-slate-11 bg-n-alpha-3 backdrop-blur-sm;
}
</style>
